type RouteEdit = () => void | Promise<void>

interface CompletedExecutionEditCoordinator {
  apply: (edit: RouteEdit) => Promise<boolean>
}

interface CompletedExecutionEditDeps {
  routeLocked: () => boolean
  completedExecution: () => boolean
  endCompletedExecution: () => Promise<boolean>
  reportCompletedExitFailure: () => void
}

export const createCompletedExecutionEditCoordinator = (deps: CompletedExecutionEditDeps) => {
  let retirementTask: Promise<boolean> | null = null

  const retireCompletedExecution = (): Promise<boolean> => {
    if (retirementTask) return retirementTask
    const task = deps
      .endCompletedExecution()
      .then((retired) => {
        if (!retired) deps.reportCompletedExitFailure()
        return retired
      })
      .catch(() => {
        deps.reportCompletedExitFailure()
        return false
      })
      .finally(() => {
        if (retirementTask === task) retirementTask = null
      })
    retirementTask = task
    return task
  }

  const apply = async (edit: RouteEdit): Promise<boolean> => {
    if (deps.routeLocked()) {
      if (!deps.completedExecution()) return false
      if (!(await retireCompletedExecution())) return false
    }
    await edit()
    return true
  }

  return { apply }
}

interface CompletedExecutionRouteActions<Token> {
  selectToken: (token: Token) => void
  handleAmountInput: (event: Event) => void
  setMaxAmount: () => void
  applyQuickAmount: (value: string) => void
}

export const createCompletedExecutionRouteHandlers = <Token>(
  coordinator: CompletedExecutionEditCoordinator,
  actions: CompletedExecutionRouteActions<Token>
) => ({
  selectToken: (token: Token): Promise<boolean> =>
    coordinator.apply(() => {
      actions.selectToken(token)
    }),
  handleAmountInput: (event: Event): Promise<boolean> => {
    const capturedValue = (event.target as HTMLInputElement).value
    return coordinator.apply(() => {
      actions.handleAmountInput({ target: { value: capturedValue } } as unknown as Event)
    })
  },
  setMaxAmount: (): Promise<boolean> => coordinator.apply(actions.setMaxAmount),
  applyQuickAmount: (value: string): Promise<boolean> =>
    coordinator.apply(() => {
      actions.applyQuickAmount(value)
    }),
})
